import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { ProductReportCtaBanner } from "@/components/site/ProductReportCtaBanner";
import { Section, SectionHeader } from "@/components/site/Blocks";
import { states, isLicensedState } from "@/data/states";
import { buildHead } from "@/lib/seo-head";
import { products } from "@/data/products";
import { LicensingDisclosure } from "@/components/site/LicensingDisclosure";
import { LiveDscrRateSection } from "@/components/rates/LiveDscrRateSection";
import { PageEssentials, PageOutro } from "@/components/site/PageEssentials";

export const Route = createFileRoute("/where-we-lend")({
  head: () =>
    buildHead({
      title: "Where We Lend | FL, CO & LLC States | Simply Approved",
      description:
        "Licensed in Florida and Colorado. LLC business-purpose lending in additional states where permitted. Click any state for program details.",
      url: "/where-we-lend",
      keywords:
        "states we lend in, Florida licensed mortgage broker, Colorado registered mortgage company, LLC business purpose loan states, DSCR loans by state, investor loan coverage map, business purpose lending states, where can I get a DSCR loan, state licensed mortgage broker, LLC-only loan states, nationwide investor lending coverage",
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "Where We Lend", url: "/where-we-lend" },
      ],
      service: { name: "Investor Loan Coverage by State" },
    }),
  component: Where,
});

function Where() {
  return (
    <SiteShell>
      <PageHero eyebrow="Coverage" title="Investor lending in Florida, Colorado, and LLC-eligible states" subtitle="Licensed in Florida and Colorado. In other states, loans are available only to LLCs and legal entities as business-purpose transactions. Every state below links to program details." />
      <PageEssentials />
      <Section>
        <div className="mb-6 rounded-xl border border-brand-gold/30 bg-brand-gold/10 p-4 text-sm">
          <LicensingDisclosure />
        </div>
        <SectionHeader eyebrow="By state" title="States where we offer business-purpose loans" />
        <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {states.map((s) => (
            <div key={s.slug} className="rounded-xl border border-border bg-card p-5">
              <div className="flex items-center justify-between">
                <div className="font-display text-lg font-bold text-brand">{s.name}</div>
                {isLicensedState(s.slug) ? (
                  <span className="rounded bg-brand px-2 py-0.5 text-xs font-semibold text-primary-foreground">Licensed</span>
                ) : (
                  <span className="rounded bg-muted px-2 py-0.5 text-xs font-semibold text-muted-foreground">LLC-only</span>
                )}
              </div>
              <div className="mt-1 text-xs text-muted-foreground">{s.topMarkets.slice(0, 3).join(" · ")} · {s.landlordFriendliness}</div>
              <div className="mt-3 grid grid-cols-2 gap-1 text-xs">
                {products.map((p) => (
                  <Link
                    key={p.slug}
                    to={`/${p.slug}/$state` as string}
                    params={{ state: s.slug }}
                    className="rounded px-2 py-1 text-brand hover:bg-muted"
                  >
                    {p.shortName} →
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Section>

      <section data-inline-cta="">
        <div className="container mx-auto max-w-7xl px-4">
          <ProductReportCtaBanner productKey="dscr" variant="inline" />
        </div>
      </section>
      <LiveDscrRateSection
        strategyCards
        id="live-rates"
        eyebrow={"Live pricing · DSCR"}
        heading={"Today's DSCR pricing in the states where we arrange loans"}
        paragraphs={[
          "These are actual pricing responses returned for a sample business-purpose DSCR scenario on non-owner-occupied investment property — rate, APR, borrower points or lender credit, estimated principal and interest, and lock period from the same quote. Pricing changes intraday and is not a loan approval, commitment, or guarantee of financing.",
        ]}
        links={[
          { to: "/investment-property-rates", label: "Price your own DSCR scenario" },
          { to: "/rates", label: "See today's investor rate bands" },
        ]}
      />
    <PageOutro />
      </SiteShell>
  );
}
